<template>
  <div class="debt-installment-pagination">
    <b-row class="debt-installment-pagination__row">
      <!-- Total Count Display -->
      <b-col 
        lg="3" 
        md="3" 
        sm="12" 
        class="debt-installment-pagination__total-col"
      >
        <p 
          class="debt-installment-pagination__total-text mt-3 text-dark"
          :aria-label="`Tổng số bản ghi: ${formatTotal(total)}`"
        >
          Tổng số: {{ formatTotal(total) }}
        </p>
      </b-col>

      <!-- Pagination Controls -->
      <b-col 
        lg="9" 
        md="9" 
        sm="12" 
        class="debt-installment-pagination__controls-col"
      >
        <b-pagination-nav
          v-show="totalPages >= 2"
          class="debt-installment-pagination__nav custom-pagination"
          :link-gen="linkGenerator"
          :number-of-pages="totalPages"
          use-router
          @change="onPageChange"
          align="right"
          first-class="page-item-first btn btn-icon btn-sm m-1"
          prev-class="page-item-prev btn btn-icon btn-sm m-1"
          next-class="page-item-next btn btn-icon btn-sm m-1"
          last-class="page-item-last btn btn-icon btn-sm m-1"
          page-class="btn btn-icon btn-sm border-0 m-1"
          :aria-label="`Điều hướng trang, hiện tại đang ở trang ${currentPage} trong tổng số ${totalPages} trang`"
        >
          <!-- First page button -->
          <template v-slot:first-text>
            <span :aria-label="'Đi đến trang đầu tiên'">
              <i class="ki ki-bold-double-arrow-back icon-xs"></i>
            </span>
          </template>

          <!-- Previous page button -->
          <template v-slot:prev-text>
            <span :aria-label="'Đi đến trang trước'">
              <i class="ki ki-bold-arrow-back icon-xs"></i>
            </span>
          </template>

          <!-- Next page button -->
          <template v-slot:next-text>
            <span :aria-label="'Đi đến trang tiếp theo'">
              <i class="ki ki-bold-arrow-next icon-xs"></i>
            </span>
          </template>

          <!-- Last page button -->
          <template v-slot:last-text>
            <span 
              class="text-info"
              :aria-label="'Đi đến trang cuối cùng'"
            >
              <i class="ki ki-bold-double-arrow-next icon-xs"></i>
            </span>
          </template>
        </b-pagination-nav>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { convertPrice } from '@/utils/common';

export default {
  name: 'DebtInstallmentPagination',
  props: {
    /**
     * Total number of items
     */
    total: {
      type: Number,
      default: 0,
      validator: (total) => total >= 0,
    },
    /**
     * Total number of pages
     */
    totalPages: {
      type: Number,
      default: 1,
      validator: (pages) => pages >= 1,
    },
    /**
     * Current page number
     */
    currentPage: {
      type: Number,
      default: 1,
      validator: (page) => page >= 1,
    },
    /**
     * Items per page
     */
    pageSize: {
      type: Number,
      default: 10,
      validator: (size) => size > 0,
    },
  },
  computed: {
    /**
     * Generate pagination links
     */
    linkGenerator() {
      return (pageNum) => {
        return pageNum === 1 ? '?' : `?page=${pageNum}`;
      };
    },
  },
  methods: {
    /**
     * Format total count for display
     * @param {number} value - Total count value
     * @returns {string} Formatted total string
     */
    formatTotal(value) {
      return convertPrice(value);
    },

    /**
     * Handle page change event
     * @param {number} page - New page number
     */
    onPageChange(page) {
      this.$emit('page-change', page);
    },

    /**
     * Handle keyboard navigation
     * @param {KeyboardEvent} event - Keyboard event
     */
    onKeyboardNavigation(event) {
      // Handle arrow key navigation for accessibility
      if (event.key === 'ArrowLeft' && this.currentPage > 1) {
        this.onPageChange(this.currentPage - 1);
      } else if (event.key === 'ArrowRight' && this.currentPage < this.totalPages) {
        this.onPageChange(this.currentPage + 1);
      } else if (event.key === 'Home') {
        this.onPageChange(1);
      } else if (event.key === 'End') {
        this.onPageChange(this.totalPages);
      }
    },
  },
  mounted() {
    // Add keyboard navigation support
    this.$el.addEventListener('keydown', this.onKeyboardNavigation);
  },
  beforeDestroy() {
    // Remove keyboard navigation listener
    this.$el.removeEventListener('keydown', this.onKeyboardNavigation);
  },
};
</script>

<style lang="scss" scoped>
.debt-installment-pagination {
  &__row {
    align-items: center;
    margin-top: 1rem;
  }

  &__total-col {
    display: flex;
    align-items: center;
  }

  &__total-text {
    font-weight: 500;
    margin-bottom: 0;
    color: #3f4254;
  }

  &__controls-col {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  &__nav {
    margin-bottom: 0;
  }
}

@media (max-width: 768px) {
  .debt-installment-pagination {
    &__row {
      flex-direction: column;
      align-items: stretch;
    }

    &__total-col {
      justify-content: center;
      margin-bottom: 1rem;
    }

    &__controls-col {
      justify-content: center;
    }

    &__total-text {
      text-align: center;
    }
  }
}
</style>
